import React, { useState } from 'react';
import './TaxYearView.css';
import { FaBuilding, FaUser, FaUserFriends, FaHouseUser, FaRestroom, FaStreetView, FaQuestion } from 'react-icons/fa';
import { Bar } from 'react-chartjs-2';



function TaxYearView({ data, allData = [] }) {
   
    // State to track image errors for employers
    const [imageErrors, setImageErrors] = useState({});

    // Handle employer image errors and fallback to FaBuilding icon
    const handleImageError = (employer) => {
        
        setImageErrors((prevErrors) => ({
            ...prevErrors,
            [employer]: true, // Mark the employer logo as failed to load
        }));
    };

    const {
        taxYear = 'N/A',
        filingStatus = 'Unknown',
        employers = [],
        residentState = '',
        otherState1 = '',
        otherState2 = '',
        taxRates: {
            effectiveTaxRate = 0,
            marginalTaxRate = 0,
        } = {},
        financialMetrics: {
            agi = 0,
            taxableIncome = 0,
            totalTax = 0,
            returnAmt = 0,
            wages = 0,
            interest = 0,
            dividends = 0,
            ira = 0,
            pension = 0,
            socialSecurity = 0,
            capitalGains = 0,
            residentStateTax = 0,
            otherStateTax1 = 0,
            otherStateTax2 = 0,
            residentStateReturn = 0,
            otherStateReturn1 = 0,
            otherStateReturn2 = 0,
        } = {},
    } = data || {}; // Handle undefined `data` by providing a default empty object



    // Filter for just income sources for plotting in Adjusted Gross Income 
    const incomeSources = {
        Wages: wages,
        Interest: interest,
        Dividends: dividends,
        IRA: ira,
        Pension: pension,
        'Social Security': socialSecurity,
        'Capital Gains': capitalGains,
    };

    const filteredSources = Object.entries(incomeSources).filter(([, value]) => value !== 0);


    //Destructuring State Data
    const statesData = [
        { name: residentState, tax: residentStateTax, returnAmt: residentStateReturn },
        { name: otherState1, tax: otherStateTax1, returnAmt: otherStateReturn1 },
        { name: otherState2, tax: otherStateTax2, returnAmt: otherStateReturn2 },
    ].filter(state => state.name); // Remove empty state entries

    // Federal Tax Data for Table
    const federalTaxData = [
        { label: "Taxable Income", value: taxableIncome },
        { label: "Federal Tax", value: totalTax },
        { label: "Federal Tax Return", value: returnAmt }
    ];


    /*Currency formatter*/
    const formatCurrency = (value) => {
        if (typeof value !== 'number' || isNaN(value)) return '$0';
        const absValue = Math.abs(value);
        const formattedValue =
            absValue >= 1000000
                ? `$${(absValue / 1000000).toFixed(1)}m`
                : absValue >= 1000
                    ? `$${(absValue / 1000).toFixed(0)}k`
                    : `$${(absValue).toFixed(0)}`;
        return value < 0 ? `-${formattedValue}` : formattedValue;
    };

    const formatCurrency_dollars = (value) => {
        if (typeof value !== 'number' || isNaN(value)) return '$0';

        const formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0
        });

        return formatter.format(value);
    };


    // Ensure `allData` is valid and calculate the rank
  
    const totalAgiRank =
        allData && allData.length > 0
            ? allData
                .map((item) => item.financialMetrics?.agi || 0)
                .sort((a, b) => b - a)
                .indexOf(agi) + 1
            : 0;
   

    const rankText =
        totalAgiRank === 1
            ? `${taxYear} was your top earning year on record!` 
            : totalAgiRank === allData.length
                ? `${taxYear} was your lowest earning year on record.`
                : totalAgiRank > 1
                    ? `${taxYear} was your ${totalAgiRank}${totalAgiRank === 2 ? 'nd' : totalAgiRank === 3 ? 'rd' : 'th'} highest earning year on record.`
                    : 'No data available for ranking';


    

    // Income Data for Widget Column 1
    const incomeData = {
        labels: filteredSources.map(([label]) => label),
        datasets: [
            {
                data: filteredSources.map(([, value]) => value),
                backgroundColor: ['#6a84a6', '#8ab6d6', '#d6a68a', '#a68ab6', '#a6d68a', '#d6d68a', '#a6a6d6'],
                hoverBackgroundColor: ['#4a6496', '#6a96c6', '#b6876a', '#886aa6', '#86b66a', '#c6c66a', '#8686c6'],
            },
        ],
    };

    const incomeOptions = {
        borderRadius: 10,
        plugins: {
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const value = context.raw;
                        const percentage = ((value / agi) * 100).toFixed(0);
                        return `${formatCurrency_dollars(value)} (${percentage}%)`;
                    },
                },
            },
            legend: { display: false },
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                grid: { display: false },
                ticks: {
                    color: '#6a6a6a',
                    font: { weight: 'bold' }
                }
            },
            y: {
                grid: { color: '#ddd' },
                ticks: {
                    color: '#6a6a6a',
                    font: { weight: 'bold' },
                    callback: (value) => formatCurrency(value),
                }
            },
        },
    };

    // Taxes Data for Taxes Widget
    const taxesData = {
        labels: ['Federal Taxes', 'State Taxes'],
        datasets: [
            {
                label: 'Federal Taxes',
                data: [totalTax, 0], // Federal taxes appear only for the first label
                backgroundColor: ['#454545'], // Federal taxes color
            },
            ...(residentStateTax !== 0
                ? [
                    {
                        label: residentState,
                        data: [0, residentStateTax], // State tax contribution
                        backgroundColor: ['#1a742a'], // Color for resident state
                    },
                ]
                : []),
            ...(otherStateTax1 !== 0
                ? [
                    {
                        label: otherState1,
                        data: [0, otherStateTax1], // State tax contribution
                        backgroundColor: ['#f28f0c'], // Color for otherState1
                    },
                ]
                : []),
            ...(otherStateTax2 !== 0
                ? [
                    {
                        label: otherState2,
                        data: [0, otherStateTax2], // State tax contribution
                        backgroundColor: ['#2a9d8f'], // Color for otherState2
                    },
                ]
                : []),
        ],
    };

    const taxesOptions = {
        borderRadius: 10,
        plugins: {
            tooltip: {
                callbacks: {
                    title: (tooltipItems) => {
                        // Tooltip title matches the dataset label
                        const datasetIndex = tooltipItems[0].datasetIndex;
                        const label = taxesData.datasets[datasetIndex].label;
                        return label; // Return the state or federal label
                    },
                    label: (context) => {
                        // Tooltip content displays the formatted tax value
                        const value = context.raw;
                        return `${formatCurrency_dollars(value)}`;
                    },
                },
            },
            legend: { display: false }, // Display legend for stacked components
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                stacked: true, // Enable stacking on the x-axis
                grid: { display: false },
                ticks: {
                    color: '#6a6a6a',
                    font: { weight: 'bold' },
                },
            },
            y: {
                stacked: true, // Enable stacking on the y-axis
                grid: { color: '#ddd' },
                ticks: {
                    color: '#6a6a6a',
                    font: { weight: 'bold' },
                    callback: (value) => formatCurrency(value),
                },
            },
        },
    };

    return (
        <div className="tax-year-view">
            <div className="banner">{taxYear} Year in Review</div>

            {/*First row of Profile Section*/ }
            <div className="profile-section">
                <div className="widget-header">Profile</div>
                <div className="row">

                    {/*Filing Status*/ }
                    <div className="profile-item">
                        <span className="label">Filing Status</span>
                        <span className="value">
                            {filingStatus === 'Single' && ( <> <FaUser /> Single </>)}
                            {filingStatus === 'Married Filing Jointly' && ( <> <FaUserFriends /> Married Filing Jointly </>)}
                            {filingStatus === 'Married Filing Separately' && ( <> <FaRestroom /> Married Filing Separately </>)}
                            {filingStatus === 'Head of Household' && ( <> <FaHouseUser /> Head of Household </>)}
                            {filingStatus === 'Qualifying Surviving Spouse' && ( <> <FaStreetView /> Qualifying Surviving Spouse </>)}
                            {!['Single', 'Married Filing Jointly', 'Married Filing Separately', 'Head of Household', 'Qualifying Surviving Spouse'].includes(filingStatus) && (
                                <>
                                    <FaQuestion /> {filingStatus || 'Unknown'}
                                </>
                            )}
                        </span>
                    </div>


                    {/* Employer Section */}
                    <div className="profile-item">
                        <span className="label">Employer</span>
                        <span className="value employer-icons">
                            {employers.length > 0 ? (
                                employers.map((employer, index) => {
                                    const formattedEmployer = employer.toLowerCase().replace(/\s/g, '-'); // Convert spaces to dashes for filename consistency
                                    const employerImagePath = `${process.env.PUBLIC_URL}/icons/employers/${formattedEmployer}.png`;
                                    
                                   
                                    return (

                                        <div key={index} className="employer-icon-container">
                                            {imageErrors[formattedEmployer] ? (
                                                <FaBuilding className="fallback-icon" />
                                            ) : (
                                                <img
                                                    src={employerImagePath}
                                                    alt={employer}
                                                    className="employer-icon"
                                                    onError={() => handleImageError(formattedEmployer)}
                                                />
                                            )}
                                            <span className="employer-name">{employer}</span>
                                        </div>
                                    );
                                })
                            ) : (
                                'None'
                            )}
                        </span>
                    </div>

                </div>


               
            </div>

            {/*First row of Profile Section*/}
            <div className="profile-section">
                <div className="widget-header">Income</div>


                {/*First Row of Profile Section*/}
                <div className="row">
                    {/* AGI*/}
                    <div className="profile-item">
                        <span className="label">Adjusted Gross Income</span>
                        <span className="value">{formatCurrency_dollars(agi)}</span>
                    </div>

                    {/*Taxable Income*/}
                    <div className="profile-item">
                        <span className="label">Taxable Income</span>
                        <span className="value">{formatCurrency_dollars(taxableIncome)}</span>
                    </div>

                </div>

                <div className="row">
                    {/* Tax Bracket*/}
                    <div className="profile-item">
                        <span className="label">Tax Bracket</span>
                        <span className="value">{marginalTaxRate || '0%'}</span>
                    </div>

                    {/* Effective Tax Rate*/}
                    <div className="profile-item">
                        <span className="label">Effective Tax Rate</span>
                        <span className="value">{effectiveTaxRate || '0%'}</span>
                    </div>
                    
                </div>

                {/* Earnings rank text*/}
                <div className="widget-footer">{rankText}</div>

            </div>

                                   
            {/* NEW: TAXES WIDGET */}
            <div className="state-tax-widget">
                <div className="widget-header">Taxes</div>
                <div className="widget-content">
                    <table className="state-tax-table">
                        <thead>
                            <tr>
                                <th>Source</th>
                                <th>Tax</th>
                                <th>Tax Return</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* FEDERAL TAX ROW */}
                            <tr>
                                <td>
                                    <div className="state-entry">
                                        <img
                                            src={`${process.env.PUBLIC_URL}/icons/irs.png`}
                                            alt="IRS"
                                            className="state-icon"
                                        />
                                        Federal
                                    </div>
                                </td>
                                <td>{formatCurrency_dollars(totalTax)}</td>
                                <td className={`tax-return ${returnAmt >= 0 ? 'Refund' : 'Payment'}`}>
                                    {formatCurrency_dollars(returnAmt)} {returnAmt >= 0 ? 'Refund' : 'Payment'}
                                </td>
                            </tr>

                            {/* STATE TAX ROWS */}
                            {statesData.length > 0 ? (
                                statesData.map((state, index) => (
                                    <tr key={index}>
                                        <td>
                                            <div className="state-entry">
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/icons/${state.name.toLowerCase().replace(/\s/g, '-') }.png`} 
                                                    alt={state.name}
                                                    className="state-icon"
                                                />
                                                {state.name}
                                            </div>
                                        </td>
                                        <td>{formatCurrency_dollars(state.tax)}</td>
                                        <td className={`tax-return ${state.returnAmt >= 0 ? 'Refund' : 'Payment'}`}>
                                            {formatCurrency_dollars(state.returnAmt)} {state.returnAmt >= 0 ? 'Refund' : 'Payment'}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="3" className="no-data">No State Tax Information Available</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* WIDGET: Income Breakdown Graph*/ }
            <div className="widgets-placeholder">
                <div className="widget-column">
                    <div className="widget-header">Income Breakdown</div>
                    <div className="widget-content">
                        <div style={{ height: '300px' }}>
                            <Bar data={incomeData} options={incomeOptions} />
                        </div>

                        
                    </div>
                    
                </div>
                <div className="widget-column">
                    <div className="widget-header">Taxes Breakdown</div>
                    <div className="widget-content">
                        <div style={{ height: '300px' }}>
                            <Bar data={taxesData} options={taxesOptions} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer for Attribution*/}
            <div className="footer-attribution">
                <a
                    href="https://www.flaticon.com/authors/jungsa"
                    title="State Icons"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    State icons created by JunGSa - Flaticon
                </a>
            </div>



        </div>
    );
  
}

export default TaxYearView;
